/**
 * <sb-carousel> — image carousel over a native scroll-snap track.
 *
 * The track is an ordinary horizontally scrolling element, so touch swiping,
 * trackpad gestures, momentum and arrow-key scrolling all work without this
 * script. The script adds the previous/next buttons, thumbnail navigation,
 * the "2 / 5" counter and keeps aria-current / button states in sync with
 * whatever slide is actually in view.
 *
 * Markup contract (sections/sb-buy.liquid):
 *   [data-track] > [data-slide]*   [data-prev] [data-next] [data-counter]
 *   [data-thumb]* (one per slide, same order)
 */
(function () {
  if (customElements.get('sb-carousel')) return;

  class SBCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.counter = this.querySelector('[data-counter]');
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.prev.addEventListener('click', () => this.goTo(this.index - 1));
      this.next.addEventListener('click', () => this.goTo(this.index + 1));
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
      });

      // Track the slide in view from the scroll position, however it got there
      // (button, thumbnail, swipe, trackpad). While a button-driven smooth
      // scroll is under way, `pending` holds its destination so the slides it
      // passes on the way don't flicker the counter or swallow rapid clicks.
      this.pending = null;
      this._frame = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          if (!this._frame) this._frame = requestAnimationFrame(() => this._onScroll());
        },
        { passive: true },
      );
      // A swipe or wheel takes over from any programmatic scroll.
      ['pointerdown', 'wheel', 'touchstart'].forEach((type) =>
        this.track.addEventListener(type, () => (this.pending = null), { passive: true }),
      );

      // Theme editor: selecting a slide block scrolls it into view.
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target.closest('[data-slide]'));
        if (i > -1) this.goTo(i, true);
      });

      this._render();
    }

    _onScroll() {
      this._frame = 0;
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (this.pending !== null) {
        if (i !== this.pending) return;
        this.pending = null;
      }
      this._setIndex(Math.min(Math.max(i, 0), this.slides.length - 1));
    }

    goTo(i, instant) {
      const count = this.slides.length;
      const target = ((i % count) + count) % count;
      // scrollTo on the track (not scrollIntoView) so the page never jumps.
      // Slides are exactly one track-width wide, so slide i starts at i widths.
      const left = target * this.track.clientWidth;
      this.pending = Math.abs(this.track.scrollLeft - left) < 1 ? null : target;
      this.track.scrollTo({ left, behavior: instant ? 'instant' : undefined });
      this._setIndex(target);
    }

    _setIndex(i) {
      if (i < 0 || i === this.index) return;
      this.index = i;
      this._render();
    }

    _render() {
      const count = this.slides.length;
      this.slides.forEach((slide, i) => {
        // Keep off-screen slides out of the tab order and the a11y tree.
        slide.toggleAttribute('inert', i !== this.index);
      });
      this.thumbs.forEach((thumb, i) => {
        if (i === this.index) {
          thumb.setAttribute('aria-current', 'true');
          // Keep the active thumbnail visible in an overflowing strip without
          // scrolling the page.
          const strip = thumb.parentElement;
          const left = thumb.offsetLeft - strip.offsetLeft;
          if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({ left: left - (strip.clientWidth - thumb.offsetWidth) / 2 });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${count}`;
    }
  }

  customElements.define('sb-carousel', SBCarousel);
})();
